import { ReactNode, useMemo } from 'react';

import {
  NavLink as RouterLink,
  useLocation,
  useParams,
} from 'react-router-dom';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import {
  Collapse,
  Grid2 as Grid,
  ListItemButton,
  ListItemIcon,
  Stack,
  Typography,
} from '@mui/material';

import usePrimaryNavStore from '@/components/shared/PrimaryNavDrawer/PrimaryNavStore';
import {
  NavItem,
  accessFilter,
  getUrl,
  navItemActiveStyling,
  navItemHoverStyling,
  navItemTransition,
} from '@/components/shared/PrimaryNavDrawer/utils';
import { useSession } from '@/providers/SessionProvider/SessionProvider';
import usePermissionAccess from '@/providers/UserPermissionsProvider/hooks/usePermissionAccess';
// TODO: feature_flag_cleanup: hasAccountPermissions - Remove this import when FF is fully rolled out
import useRoleAccess from '@/providers/UserPermissionsProvider/hooks/useRoleAccess';

import PrimaryNavLinksSkeleton from './PrimaryNavLinksSkeleton';

type PrimaryNavLinksProps = {
  iconMap: Record<string, ReactNode>;
  navItems: NavItem[];
};

const PrimaryNavLinks = ({ iconMap, navItems }: PrimaryNavLinksProps) => {
  const { isDrawerOpen, navItemsState, setNavItemsState } =
    usePrimaryNavStore();
  const { clientId = '' } = useParams<{ clientId: string }>();
  const {
    currentUser,
    currentClient,
    isCurrentClientLoading,
    isOrgUsersLoading,
    isUserPermissionsLoading,
  } = useSession();
  const { hasPermission } = usePermissionAccess();
  // TODO: feature_flag_cleanup: hasAccountPermissions - Remove useRoleAccess when FF is fully rolled out
  const { hasRole } = useRoleAccess();
  const location = useLocation();

  const hasAccountPermissions = currentClient?.hasAccountPermissions ?? false;

  // Show loading state until all required data is loaded
  const isLoading =
    isCurrentClientLoading || isOrgUsersLoading || isUserPermissionsLoading;

  const handleNavItemClick = (navItem: string) => {
    if (navItemsState.includes(navItem)) {
      const newState = navItemsState.includes(navItem)
        ? navItemsState.filter((currTitle) => currTitle !== navItem)
        : [...navItemsState, navItem];
      setNavItemsState(newState);
    } else {
      setNavItemsState([navItem]);
    }
  };

  const visibleNavItems = useMemo(() => {
    return navItems.filter((item) =>
      accessFilter({
        currentClient,
        currentUser,
        navItem: item,
        hasAccountPermissions,
        hasPermission,
        hasRole,
      }),
    );
  }, [
    currentClient,
    currentUser,
    navItems,
    hasAccountPermissions,
    hasPermission,
    hasRole,
  ]);

  const visibleSubItems = (subItems: NavItem[] | undefined) => {
    if (!subItems) return [];
    return subItems.filter((item) =>
      accessFilter({
        currentClient,
        currentUser,
        navItem: item,
        hasAccountPermissions,
        hasPermission,
        hasRole,
      }),
    );
  };

  const isParentActive = (
    subItems: NavItem[] | undefined,
    shouldSwitchClients: boolean | undefined,
  ) => {
    if (!subItems) return false;
    return subItems.some(
      ({ route, domains }) =>
        route &&
        location.pathname.includes(
          getUrl(clientId, route, domains, false, shouldSwitchClients),
        ),
    );
  };

  if (isLoading) {
    return <PrimaryNavLinksSkeleton count={navItems.length} />;
  }

  return (
    <>
      {visibleNavItems.map(
        ({
          id,
          title,
          route,
          subItems,
          domains,
          titleDecoration,
          shouldSwitchClients,
        }) => (
          <li key={id}>
            <ListItemButton
              onClick={() => handleNavItemClick(id)}
              {...(route && {
                component: RouterLink,
                to: getUrl(
                  clientId,
                  route,
                  domains,
                  false,
                  shouldSwitchClients,
                ),
              })}
              sx={{
                ...(isParentActive(subItems, shouldSwitchClients) &&
                  !isDrawerOpen &&
                  navItemActiveStyling),
                py: '6px',
                px: '12px',
                height: 36,
                '&:hover': {
                  ...navItemHoverStyling,
                  '& .arrow-icon': {
                    color: 'white',
                  },
                },
                '&.active': navItemActiveStyling,
              }}
              data-cy={`${id}-nav-item`}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                }}
              >
                {iconMap[id]}
              </ListItemIcon>
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  ...navItemTransition(isDrawerOpen),
                }}
              >
                <Stack
                  direction="row"
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ color: 'white', letterSpacing: 0.15 }}>
                    {title}
                  </Typography>
                  {titleDecoration !== null && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: '600',
                        ml: 1,
                        color: 'cyan400',
                        textDecoration: 'none',
                      }}
                    >
                      {titleDecoration}
                    </Typography>
                  )}
                </Stack>
                {subItems ? (
                  navItemsState.includes(id) ? (
                    <ArrowDropUpIcon
                      className="arrow-icon"
                      sx={{ color: 'neutral400' }}
                    />
                  ) : (
                    <ArrowDropDownIcon
                      className="arrow-icon"
                      sx={{ color: 'neutral400' }}
                    />
                  )
                ) : null}
              </Stack>
            </ListItemButton>

            <Collapse
              in={navItemsState.includes(id) && isDrawerOpen}
              timeout="auto"
              unmountOnExit
              sx={{ my: visibleSubItems(subItems).length ? 0.5 : 0 }}
            >
              <Stack component="ul" sx={{ px: 0, rowGap: 0.25 }}>
                {visibleSubItems(subItems).map(
                  ({
                    id: subItemId,
                    title: subItemTitle,
                    titleDecoration: subItemTitleDecoration,
                    header: subItemHeader,
                    route: subItemRoute,
                    domains,
                    shouldSwitchClients,
                    action,
                  }) => (
                    <ListItemButton
                      key={subItemId}
                      sx={{
                        py: '6px',
                        pl: '52px',
                        '&:hover': navItemHoverStyling,
                        '&.active': navItemActiveStyling,
                        justifyContent: 'center',
                      }}
                      onClick={() => action?.()}
                      {...(subItemRoute && {
                        component: RouterLink,
                        to: getUrl(
                          clientId,
                          subItemRoute,
                          domains,
                          false,
                          shouldSwitchClients,
                        ),
                      })}
                      data-cy={`${subItemId}-sub-item`}
                    >
                      <Grid
                        container
                        columnSpacing={1}
                        sx={{
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        {subItemHeader !== null && (
                          <Grid size={{ xs: 12 }}>
                            <Typography variant="caption" color="neutral500">
                              {subItemHeader}
                            </Typography>
                          </Grid>
                        )}
                        <Grid size="grow">
                          <Stack direction="row" spacing={1}>
                            <Typography
                              sx={{
                                color: 'white',
                                textDecoration: 'none',
                                letterSpacing: 0.17,
                                fontSize: 14,
                              }}
                            >
                              {subItemTitle}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: '600',
                                color: 'cyan400',
                                textDecoration: 'none',
                                pt: 0.1,
                              }}
                            >
                              {subItemTitleDecoration}
                            </Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                    </ListItemButton>
                  ),
                )}
              </Stack>
            </Collapse>
          </li>
        ),
      )}
    </>
  );
};

export default PrimaryNavLinks;
